import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppButton, Divider, FigmaTypography, LoadingSpinner } from "@libs/zapi-ui/custom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Label } from "@libs/zapi-ui/shadcn";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { languageOptions } from "@/zapi-frontend/widgets/CreateProjectForm/const/const";
import { useUserPreferences, TUserLanguage } from "../services/useUserPreferences";
import { useUpdateUserPreferences } from "../services/useUpdateUserPreferences";

export function PreferencesSection() {
  const { t } = useTranslation("settings");
  const { refetch: refetchMe } = useMeStore();
  const queryClient = useQueryClient();
  const { data: preferences, isLoading } = useUserPreferences();
  const [uiLanguage, setUiLanguage] = useState<TUserLanguage>("english");

  useEffect(() => {
    if (preferences) setUiLanguage(preferences.uiLanguage);
  }, [preferences]);

  const { mutate: updatePreferences, isPending } = useUpdateUserPreferences({
    onSuccess: async (updated) => {
      queryClient.setQueryData(["userPreferences"], updated);
      await refetchMe?.();
      // The interface switches in an effect that has not run yet, so `t` would still resolve
      // against the outgoing language. Announce the save in the language just chosen.
      const savedT = i18n.getFixedT(projectLanguageToLng(updated.uiLanguage), "settings");
      toast.success(savedT("preferences.saved"));
    },
    onError: (error) => toast.error(error.response?.data?.message ?? t("preferences.saveFailed"))
  });

  const isChanged = Boolean(preferences) && uiLanguage !== preferences?.uiLanguage;

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col gap-4 max-w-[640px]">
      <FigmaTypography variant="heading-3" color="zinc900">
        {t("preferences.heading")}
      </FigmaTypography>
      <Divider />

      <div className="flex flex-col gap-2">
        <Label htmlFor="ui-language">{t("preferences.uiLanguage")}</Label>
        <Select
          value={uiLanguage}
          onValueChange={(value) => setUiLanguage(value as TUserLanguage)}
          disabled={isPending}
        >
          <SelectTrigger id="ui-language" className="w-[250px]" data-testid="preferences-ui-language-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languageOptions.map((option) => {
              const name = t(`languageName.${option.value}`);
              return (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`preferences-ui-language-${option.value}-item`}
                >
                  {name === option.endonym ? name : `${name} (${option.endonym})`}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <FigmaTypography variant="body-3" color="zinc500">
          {t("preferences.uiLanguageHint")}
        </FigmaTypography>
      </div>

      <div>
        <AppButton
          data-testid="preferences-save-button"
          onClick={() => updatePreferences({ uiLanguage })}
          disabled={!isChanged || isPending}
          isLoading={isPending}
          loadingText={t("common.saving")}
        >
          {t("common.save")}
        </AppButton>
      </div>
    </div>
  );
}
